/*
 * Copyright (c) 2023-2026.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import React from 'react';
import PopOverComponent from "../UIGeneratorComponents/PopOverComponent.jsx";
import SmallDialogComponent from "../UIGeneratorComponents/SmallDialogComponent.jsx";
import DropdownComponent from "../UIGeneratorComponents/DropdownComponent.jsx";
import FixedDialogComponent from "../UIGeneratorComponents/FixedDialogComponent.jsx";
import InfoDialogComponent from "../UIGeneratorComponents/InfoDialogComponent.jsx";
import FakeCursor from "../FakeCursor.jsx";
import SlideOverComponent from "../SlideOverComponent.jsx";
import SmallWindowComponent from "../UIGeneratorComponents/SmallWindowComponent.jsx";
import CameraCaptureImage from "../UIGeneratorComponents/CameraCaptureImage.jsx";
import SystemCursor from "../SystemCursor.jsx";
import useUIGeneratorState from "../../stores/UIGeneratorStore.jsx";
import {useCompare} from "../../hooks/useCompare.jsx";
import Strings from "toolbox/strings";

const UIElement = ({uiId}) => {

    const element = useUIGeneratorState(useCompare(state => state.getUIElement(uiId)));

    const removeUIElement = useUIGeneratorState(state => state.removeUIElement);
    const updateUIElementState = useUIGeneratorState(state => state.updateUIElementState);


    const onClose = () => removeUIElement(uiId);

    const displayContent = () => {
        if (Strings.isEmpty(element.content)) return null;
        return element.content(
            element.state,
            (newState) => updateUIElementState(uiId, newState),
            onClose
        );
    };

    if (Strings.isUndefined(element)) {
        return <></>
    }

    const {settings = {}} = element ?? {};

    const buttons = (settings.button ?? []).map(item => ({
        ...item,
        onClick: () => item.onClick(element?.state, (newState) => updateUIElementState(uiId, newState), onClose),
        isActive: item?.isActive !== undefined ? item?.isActive(element?.state) : true,
    }));

    switch (element?.type) {
        case 'modal':
            return (
                <PopOverComponent
                    isOpen={true}
                    uiId={uiId}
                    width={settings?.width}
                    withBackgroundOverlay={true}
                    key={uiId}
                    title={settings.title ?? "New modal"}
                    button={buttons}
                    onClose={onClose}
                >
                    {displayContent()}
                </PopOverComponent>
            );
        case 'miniDialog':
            return (
                <SmallDialogComponent
                    isOpen={true}
                    key={uiId}
                    uiId={uiId}
                    querySelector={settings.querySelector}
                    button={buttons}
                    title={settings.title ?? "New Dialog"}
                >
                    {displayContent()}
                </SmallDialogComponent>
            );
        case 'dropdown':
            return (
                <DropdownComponent
                    isOpen={true}
                    key={uiId}
                    querySelector={settings.querySelector}
                    close={onClose}
                    text={settings.text ?? "New Dropdown"}
                    content={element.content(onClose)}
                >
                    {displayContent()}
                </DropdownComponent>
            );
        case 'fixedDialog':
            return (
                <FixedDialogComponent
                    isOpen={true}
                    key={uiId}
                    closeTimeout={settings.closeTimeout}
                    onButtonClick={settings.onButtonClick || (() => {
                    })}
                    close={onClose}
                    title={settings.title ?? "New Dialog"}
                    buttonText={settings.buttonText ?? "Button 1"}
                />
            );
        case 'infoDialog':
            return (
                <InfoDialogComponent
                    isOpen={true}
                    key={uiId}
                    close={onClose}
                    closeTimeout={settings.closeTimeout}
                    title={settings.title ?? "Info Text"}
                />
            );
        case 'fakeCursor':
            return (
                <FakeCursor
                    key={uiId}
                    iconName={settings.iconName ?? ""}
                    display={true}
                    color={settings.color}
                    size={settings.size}
                    startPos={(settings.startPos ?? {
                        clientX: 0, clientY: 0
                    })}
                    allowClick={settings.allowClick ?? false}
                    lockY={settings.lockY ?? false}
                    lockX={settings.lockX ?? false}
                />
            );
        case 'systemCursor':
            return (
                <SystemCursor
                    key={uiId}
                    uiId={uiId}
                    iconName={settings.iconName ?? ""}
                    color={settings.color}
                    size={settings.size}
                    close={onClose}
                    querySelector={settings.querySelector}
                />
            );
        case 'drawer':
            return (
                <SlideOverComponent
                    isOpen={true}
                    key={uiId}
                    uiId={uiId}
                    width={settings?.width}
                    title={settings?.title}
                    position={settings?.position}
                    setFocus={settings?.setFocus ?? true}
                    button={buttons}
                    onClose={onClose}
                    withBackgroundOverlay={true}
                >
                    {displayContent()}
                </SlideOverComponent>
            );
        case 'smallWindow':
            return (
                <SmallWindowComponent
                    isOpen={true}
                    key={uiId}
                    uiId={uiId}
                    width={settings?.width}
                    title={settings?.title}
                    onClose={onClose}
                >
                    {displayContent()}
                </SmallWindowComponent>
            );
        case 'cameraCaptureImage':
            return (
                <CameraCaptureImage
                    isOpen={true}
                    key={uiId}
                    uiId={uiId}
                    onCapture={(image) => {
                        onClose(image);
                    }}
                    basePath={settings?.basePath}
                    title={settings?.title}
                    onClose={() => {
                        removeUIElement(uiId);
                    }}
                />
            );
        default:
            console.error("The UI element type was not found.")
            return null;
    }
};


UIElement.propTypes = {};

export default UIElement;